import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';

import Button from '@polyai/ui/components/atoms/Button';
import Input from '@polyai/ui/components/atoms/Input';
import InputWrapper from '@polyai/ui/components/atoms/InputWrapper';

import FormProvider from '../../../components/molecules/Form';
import { OPENTABLE_NEW_AGENT_UI_COPY } from '../OpenTableNewAgent.uiCopy';
import { type WebsiteUrlFormData, websiteUrlSchema } from '../types';

interface WebsiteUrlFormProps {
  onSubmit: (data: WebsiteUrlFormData) => void;
  isSubmitting: boolean;
  websiteUrl?: string;
}

export default function WebsiteUrlForm({
  onSubmit,
  isSubmitting,
  websiteUrl = '',
}: WebsiteUrlFormProps) {
  const methods = useForm<WebsiteUrlFormData>({
    resolver: zodResolver(websiteUrlSchema),
    defaultValues: {
      websiteUrl: websiteUrl,
    },
  });

  const currentUrl = methods.watch('websiteUrl');

  return (
    <div className="flex flex-col gap-sm4">
      <div className="flex flex-col gap-sm2">
        <h2 className="text-header-large font-semibold text-primary leading-tight">
          {OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.title}{' '}
          <span className="text-helper">
            {OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.titleOptional}
          </span>
        </h2>
        <h3 className="text-body-regular text-secondary">
          {OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.description}
        </h3>
      </div>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Controller
          control={methods.control}
          name="websiteUrl"
          render={({ field, fieldState: { error } }) => (
            <InputWrapper
              errors={error ? [error.message ?? ''] : []}
              help={OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.tooltip}
              label={OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.label}
            >
              <Input
                {...field}
                inputMode="url"
                placeholder={OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.placeholder}
                type="url"
                fullWidth
              />
            </InputWrapper>
          )}
        />
        <Button
          className="mt-sm4"
          disabled={isSubmitting}
          label={
            currentUrl?.trim()
              ? OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.continueCta
              : OPENTABLE_NEW_AGENT_UI_COPY.websiteUrl.skipCta
          }
          type="submit"
          variant="PRIMARY"
          fullWidth
        />
      </FormProvider>
    </div>
  );
}
